import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import SEOHead from "../../components/SEOHead";
import "./app-landing.css";

/**
 * App landing page (route: /app) — QR code destination printed on the
 * Coober Pedy App corflute signage (Umoona entry/ticket desk, museum walls,
 * A-frame, caravan parks, coach parking). Single job: get the visitor into
 * the App Store or Google Play in one tap.
 *
 * Fully self-contained — no MarketingShell/SiteHeader/SiteFooter. Own dark
 * theme, own sticky mini-header and sticky download CTA.
 *
 * Ground truth: design_handoff_app_landing_page/index.html (high-fidelity,
 * static prototype) — recreated here as a native React page.
 */

const IOS_URL = "https://apps.apple.com/au/app/coober-pedy/id1461756347";
const PLAY_URL = "https://play.google.com/store/apps/details?id=com.cooberpedy.app";

const schemaData = {
  "@context": "https://schema.org",
  "@type": "MobileApplication",
  name: "Coober Pedy App",
  operatingSystem: "iOS, Android",
  applicationCategory: "TravelApplication",
  description:
    "Free travel guide app for Coober Pedy: mine tours and live booking, the opal story, local business directory, local news, town map and the Opal Quiz.",
  offers: {
    "@type": "Offer",
    price: "0",
    priceCurrency: "AUD",
  },
  publisher: {
    "@type": "Organization",
    name: "Umoona Opal Mine & Museum",
  },
};

const FEATURES = [
  {
    label: "Plan your visit",
    color: "#F4A06B",
    title: "Mine tours & live booking",
    body: "Daily 10am · 2pm · 4pm at Umoona, booked in a couple of taps.",
  },
  {
    label: "Learn",
    color: "#18BFC6",
    title: "The whole opal story",
    body: "What opal is, how it's mined, and how to buy without being ripped off.",
  },
  {
    label: "Around town",
    color: "#1BAB88",
    title: "Business directory & local news",
    body: "Who's open, where to eat, sleep and fuel up — plus what's on this week.",
  },
  {
    label: "Explore",
    color: "#2596C9",
    title: "Town map & the Opal Quiz",
    body: "Find your way underground, then earn XP and badges as you go.",
  },
];

export default function RAppLandingPage() {
  const [cta, setCta] = useState({ href: IOS_URL, label: "Download the free app" });

  useEffect(() => {
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) || (/Mac/.test(ua) && navigator.maxTouchPoints > 1);
    const isAndroid = /Android/.test(ua);
    if (isAndroid) {
      setCta({ href: PLAY_URL, label: "Get it on Google Play" });
    } else if (isIOS) {
      setCta({ href: IOS_URL, label: "Download free on iPhone" });
    }
  }, []);

  return (
    <div className="applp-page">
      <SEOHead
        title="Coober Pedy Mobile App — free travel guide, opal story & Umoona mine tours"
        description="Download the free Coober Pedy app: mine tours and live booking, the whole opal story, local business directory, local news, town map and the Opal Quiz. iOS and Android."
        canonical="/app"
        ogImage="/design/assets/app-landing/og-image.png"
        schemaData={schemaData}
      />
      <Helmet>
        <meta name="theme-color" content="#0A0705" />
        <meta property="og:image:type" content="image/png" />
      </Helmet>

      <div className="applp-col">
        <div className="applp-bar">
          <img
            src="/design/assets/umoona-white.png"
            alt="Umoona Opal Mine & Museum"
            style={{ height: 34, width: "auto" }}
          />
          <span className="applp-chip">Free app</span>
        </div>

        <div className="applp-hero">
          <img className="applp-hero-bg" src="/design/assets/app-landing/hero-cave.jpg" alt="" />
          <div className="applp-hero-scrim" />
          <img className="applp-hero-grit" src="/design/assets/app-landing/shaft-grit.png" alt="" />
          <div className="applp-hero-rail" />
          <div className="applp-hero-inner">
            <div className="applp-eyebrow">The opal capital of the world</div>
            <h1 className="applp-h1">
              Coober
              <br />
              Pedy
              <br />
              <span className="applp-stamp">Mobile App</span>
            </h1>
            <p className="applp-lede">
              The town that lives below — in your pocket. Mine tours, the whole opal story,
              local news and a quiz that rewards you for exploring.
            </p>

            <div className="applp-stores">
              <a className="applp-badge" href={IOS_URL}>
                <svg viewBox="0 0 24 24" fill="#160F09" aria-hidden="true">
                  <path d="M17.05 12.54c.02-2.2 1.8-3.26 1.88-3.31-1.03-1.5-2.62-1.71-3.19-1.73-1.36-.14-2.65.8-3.34.8-.69 0-1.75-.78-2.88-.76-1.48.02-2.85.86-3.61 2.18-1.54 2.67-.39 6.62 1.11 8.79.73 1.06 1.6 2.25 2.75 2.21 1.1-.05 1.52-.71 2.85-.71 1.33 0 1.71.71 2.88.69 1.19-.02 1.94-1.08 2.67-2.15.84-1.23 1.19-2.42 1.21-2.48-.03-.01-2.32-.89-2.34-3.53zM14.9 6.1c.61-.74 1.02-1.77.91-2.79-.88.04-1.94.59-2.57 1.32-.56.65-1.05 1.69-.92 2.69.98.08 1.98-.5 2.58-1.22z" />
                </svg>
                <span className="applp-badge-lines">
                  <span className="applp-badge-small">Download on the</span>
                  <span className="applp-badge-big">App Store</span>
                </span>
              </a>
              <a className="applp-badge" href={PLAY_URL}>
                <svg viewBox="0 0 512 512" aria-hidden="true" style={{ width: 24, height: 24 }}>
                  <path d="M43 18c-5 5-8 13-8 23v430c0 10 3 18 8 23l242-238L43 18z" fill="#00A0FF" />
                  <path d="M366 337 285 256l81-81 96 55c19 11 19 41 0 52l-96 55z" fill="#FFC900" />
                  <path d="M366 337 285 256 43 494c8 8 21 9 36 1l287-158z" fill="#FF3A44" />
                  <path d="M366 175 79 17c-15-8-28-7-36 1l242 238 81-81z" fill="#32BB6A" />
                </svg>
                <span className="applp-badge-lines">
                  <span className="applp-badge-small">Get it on</span>
                  <span className="applp-badge-big">Google Play</span>
                </span>
              </a>
            </div>
            <div className="applp-note">Free · No sign-up · iOS &amp; Android</div>
          </div>
        </div>

        <div className="applp-shot-wrap">
          <div className="applp-shot">
            <img src="/design/assets/app-landing/app-home-screenshot.png" alt="Coober Pedy app home screen" />
          </div>
        </div>

        <div className="applp-features">
          <div className="applp-kicker">What's inside</div>
          <div className="applp-grid">
            {FEATURES.map((f) => (
              <div className="applp-card" key={f.title}>
                <div className="applp-card-label" style={{ color: f.color }}>
                  {f.label}
                </div>
                <div className="applp-card-title">{f.title}</div>
                <div className="applp-card-body">{f.body}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="applp-visit">
          <div className="applp-visit-kicker">While you're in town</div>
          <h2>Umoona Opal Mine &amp; Museum</h2>
          <div className="applp-visit-body">
            Go underground on a guided mine tour. Museum and showroom are free, and there's
            parking for cars and coaches.
          </div>
          <div className="applp-pill-row">
            <a className="applp-pill applp-pill-solid" href="tel:+61886725288">
              📞 (08) 8672 5288
            </a>
            <a
              className="applp-pill applp-pill-ghost"
              href="https://maps.google.com/?q=14+Hutchison+St+Coober+Pedy+SA+5723"
            >
              📍 14 Hutchison St
            </a>
          </div>
          <div className="applp-note applp-visit-hours">Open daily · 8:30am – 5:30pm</div>
        </div>

        <footer className="applp-footer">
          <a className="applp-footer-link" href="https://umoonaopalmine.com.au">
            umoonaopalmine.com.au
          </a>
          <div className="applp-footer-credit">
            <span>App by</span>
            <img
              src="/design/assets/app-landing/turbo-logo-white.png"
              alt="Turbo 360"
              style={{ height: 18, width: "auto", opacity: 0.75 }}
            />
          </div>
        </footer>
      </div>

      <div className="applp-sticky-cta">
        <a href={cta.href}>{cta.label}</a>
      </div>
    </div>
  );
}
